import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type {
  DriverComplianceDocType,
  DriverComplianceDocument,
  DriverComplianceState,
  DriverInvite,
  DriverListEntry,
  FleetMember,
} from '@breaker/shared-types';
import type {
  CreateDriverInviteBody,
  UpdateMemberBody,
  UpsertDriverComplianceBody,
} from '@breaker/shared-schemas';

// Domain root key: ['drivers', fleetId] — mutations invalidate the root.

export function useDriversList(fleetId: string | null) {
  return useQuery({
    queryKey: ['drivers', fleetId ?? '', 'list'] as const,
    enabled: Boolean(fleetId),
    queryFn: () => api<{ drivers: DriverListEntry[] }>(`/v1/fleets/${fleetId}/drivers`),
    select: (data) => data.drivers,
    staleTime: 30_000,
  });
}

/** The whole team (Team unit): every non-left member, any badge set. */
export function useTeamList(fleetId: string | null) {
  return useQuery({
    queryKey: ['drivers', fleetId ?? '', 'team'] as const,
    enabled: Boolean(fleetId),
    queryFn: () => api<{ members: DriverListEntry[] }>(`/v1/fleets/${fleetId}/team`),
    select: (data) => data.members,
    staleTime: 30_000,
  });
}

export function useDriverDetail(fleetId: string | null, memberId: string | null) {
  return useQuery({
    queryKey: ['drivers', fleetId ?? '', 'detail', memberId ?? ''] as const,
    enabled: Boolean(fleetId && memberId),
    queryFn: () =>
      api<{ driver: DriverListEntry }>(`/v1/fleets/${fleetId}/drivers/${memberId}`),
    select: (data) => data.driver,
    staleTime: 30_000,
  });
}

export function useDriverInvites(fleetId: string | null) {
  return useQuery({
    queryKey: ['drivers', fleetId ?? '', 'invites'] as const,
    enabled: Boolean(fleetId),
    queryFn: () => api<{ invites: DriverInvite[] }>(`/v1/fleets/${fleetId}/drivers/invites`),
    select: (data) => data.invites,
    staleTime: 30_000,
  });
}

/** A live (redeemable) invite: unaccepted, unrevoked, unexpired. */
export function isInviteLive(invite: DriverInvite): boolean {
  return (
    !invite.acceptedAt &&
    !invite.revokedAt &&
    Date.parse(invite.expiresAt) > Date.now()
  );
}

export function useInviteDriver(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDriverInviteBody) =>
      api<{ invite: DriverInvite }>(`/v1/fleets/${fleetId}/drivers/invites`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] }),
  });
}

export function useRevokeDriverInvite(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      api<void>(`/v1/fleets/${fleetId}/drivers/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] }),
  });
}

export function useUpdateDriver(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, body }: { memberId: string; body: UpdateMemberBody }) =>
      api<{ driver: FleetMember }>(`/v1/fleets/${fleetId}/drivers/${memberId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] }),
  });
}

/** Doc + the short-lived presigned view URL the API serves (V2-U4). */
export type DriverComplianceDocumentWithUrl = DriverComplianceDocument & {
  url: string | null;
};

export interface DriverComplianceView {
  documents: DriverComplianceDocumentWithUrl[];
  state: DriverComplianceState;
}

export function useDriverCompliance(fleetId: string | null, memberId: string | null) {
  return useQuery({
    queryKey: ['drivers', fleetId ?? '', 'compliance', memberId ?? ''] as const,
    enabled: Boolean(fleetId && memberId),
    queryFn: () =>
      api<DriverComplianceView>(`/v1/fleets/${fleetId}/drivers/${memberId}/compliance`),
    staleTime: 30_000,
  });
}

export function useUpsertDriverCompliance(fleetId: string | null, memberId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      body,
    }: {
      type: DriverComplianceDocType;
      body: UpsertDriverComplianceBody;
    }) =>
      api<{ document: DriverComplianceDocument }>(
        `/v1/fleets/${fleetId}/drivers/${memberId}/compliance/${type}`,
        { method: 'PUT', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] }),
  });
}

/** V2-U6: confirm a driver's uploaded certificate — records the dated
 *  details the file evidences. 409s when nothing is uploaded. */
export function useConfirmDriverCompliance(fleetId: string | null, memberId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      body,
    }: {
      type: DriverComplianceDocType;
      body: {
        expiresAt: string;
        reference?: string | null;
        insurer?: string | null;
        issuedAt?: string | null;
      };
    }) =>
      api<{ document: DriverComplianceDocument }>(
        `/v1/fleets/${fleetId}/drivers/${memberId}/compliance/${type}/confirm`,
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] }),
  });
}

export function useRemoveDriverCompliance(fleetId: string | null, memberId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: DriverComplianceDocType) =>
      api<void>(`/v1/fleets/${fleetId}/drivers/${memberId}/compliance/${type}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', fleetId ?? ''] }),
  });
}
